import {
  getLeoUuid,
  LeoUUID,
  getString,
  getObject,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { SurveyLocalizedText, SurveyLocalizedTextDTO } from "../assets/surveyLocalizedText";
/* eslint-disable import/export */
export abstract class SaveDraftRPC implements
  LeoRPC<SaveDraftRPC.Request, SaveDraftRPC.Response, SaveDraftRPC.Errors.Errors>
{
  abstract execute(
    request: SaveDraftRPC.Request
  ): Promise<LeoRPCResult<SaveDraftRPC.Response, SaveDraftRPC.Errors.Errors>>;
}

export namespace SaveDraftRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    surveyLinkId: string;
    currentQuestionId: string;
    partialAnswerJson: string;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isSaveDraftRPCRequest: boolean = true;
  
    readonly surveyLinkId: LeoUUID;
    readonly currentQuestionId: LeoUUID;
    readonly partialAnswerJson: string;
  
  
    constructor(
    surveyLinkId: LeoUUID, 
    currentQuestionId: LeoUUID, 
    partialAnswerJson: string
    ) {
      this.surveyLinkId = surveyLinkId;
      this.currentQuestionId = currentQuestionId;
      Request.validatePartialAnswerJson(partialAnswerJson);
      this.partialAnswerJson = partialAnswerJson;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const surveyLinkId = getLeoUuid(dto, "surveyLinkId", InvalidRequestSurveyLinkIdError);
      const currentQuestionId = getLeoUuid(dto, "currentQuestionId", InvalidRequestCurrentQuestionIdError);
      const partialAnswerJson = getString(dto, "partialAnswerJson", InvalidRequestPartialAnswerJsonError);
      return new Request(
        surveyLinkId,
        currentQuestionId,
        partialAnswerJson
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          surveyLinkId: this.surveyLinkId.toDTO(),
          currentQuestionId: this.currentQuestionId.toDTO(),
          partialAnswerJson: this.partialAnswerJson
      };
      return dto;
    }
    private static validatePartialAnswerJson = (partialAnswerJson: string): void => {
      if(partialAnswerJson.trim().length === 0) {
        throw new InvalidRequestPartialAnswerJsonNonEmptyError(
          `Attribute partialAnswerJson cannot be an empty string.`
        );
      }
    }
    
  }
  
  Request.prototype.toString = function toString(): string {
    return `SaveDraftRPC(surveyLinkId='${this.surveyLinkId}',currentQuestionId='${this.currentQuestionId}',partialAnswerJson='${this.partialAnswerJson}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {}

  export class Response implements LeoRPCResponse {
    static fromDTO(): Response {
      return new Response();
    }

    toDTO(): ResponseDTO {
      return {}
    }
  }

  Response.prototype.toString = function toString(): string {
    return `SaveDraftRPC.Response()`;
  };

  export enum RPCError {
    InvalidSurveyLinkId = "INVALID_SURVEY_LINK_ID",
    SurveyIsClosed = "SURVEY_IS_CLOSED",
    InvalidQuestionId = "INVALID_QUESTION_ID",
    InvalidAnswerJson = "INVALID_ANSWER_JSON",
    SurveyAlreadySubmitted = "SURVEY_ALREADY_SUBMITTED"
  }

  export namespace Errors {
    
    export interface InvalidSurveyLinkIdDTO {
      code: string;
    }
    
    export class InvalidSurveyLinkId extends LeoRPCError<RPCError> {
      readonly isInvalidSurveyLinkId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSurveyLinkId;
        super(code);
      }
    
    
      toDTO(): InvalidSurveyLinkIdDTO {
        const dto: InvalidSurveyLinkIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSurveyLinkId.prototype.toString = function toString(): string {
      return `SaveDraftRPC.INVALID_SURVEY_LINK_ID()`;
    };
    
    
    export interface SurveyIsClosedDTO {
      code: string;
      surveyIsClosedTitleText: SurveyLocalizedTextDTO;
      surveyIsClosedDescriptionText: SurveyLocalizedTextDTO;
    }
    
    export class SurveyIsClosed extends LeoRPCError<RPCError> {
      readonly isSurveyIsClosed: boolean = true;
    
      readonly surveyIsClosedTitleText: SurveyLocalizedText;
      readonly surveyIsClosedDescriptionText: SurveyLocalizedText;
    
      constructor(
      surveyIsClosedTitleText: SurveyLocalizedText, 
      surveyIsClosedDescriptionText: SurveyLocalizedText
      ) {
        const code = RPCError.SurveyIsClosed;
        super(code);
        this.surveyIsClosedTitleText = surveyIsClosedTitleText;
        this.surveyIsClosedDescriptionText = surveyIsClosedDescriptionText;
      }
    
      static fromDTO(dto: SurveyIsClosedDTO): SurveyIsClosed {
        const surveyIsClosedTitleText = SurveyLocalizedText.fromDTO(getObject(dto, "surveyIsClosedTitleText", InvalidResponseSurveyIsClosedSurveyIsClosedTitleTextError));
        const surveyIsClosedDescriptionText = SurveyLocalizedText.fromDTO(getObject(dto, "surveyIsClosedDescriptionText", InvalidResponseSurveyIsClosedSurveyIsClosedDescriptionTextError));
        return new SurveyIsClosed(
          surveyIsClosedTitleText,
          surveyIsClosedDescriptionText
        );
      }
    
      toDTO(): SurveyIsClosedDTO {
        const dto: SurveyIsClosedDTO = {
          code: this.code,
          surveyIsClosedTitleText: this.surveyIsClosedTitleText.toDTO(),
          surveyIsClosedDescriptionText: this.surveyIsClosedDescriptionText.toDTO()
        };
        return dto;
      }
    
      }
    
    SurveyIsClosed.prototype.toString = function toString(): string {
      return `SaveDraftRPC.SURVEY_IS_CLOSED(surveyIsClosedTitleText='${this.surveyIsClosedTitleText}',surveyIsClosedDescriptionText='${this.surveyIsClosedDescriptionText}')`;
    };
    
    
    export interface InvalidQuestionIdDTO {
      code: string;
    }
    
    export class InvalidQuestionId extends LeoRPCError<RPCError> {
      readonly isInvalidQuestionId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidQuestionId;
        super(code);
      }
    
    
      toDTO(): InvalidQuestionIdDTO {
        const dto: InvalidQuestionIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidQuestionId.prototype.toString = function toString(): string {
      return `SaveDraftRPC.INVALID_QUESTION_ID()`;
    };
    
    
    export interface InvalidAnswerJsonDTO {
      code: string;
    }
    
    export class InvalidAnswerJson extends LeoRPCError<RPCError> {
      readonly isInvalidAnswerJson: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidAnswerJson;
        super(code);
      }
    
    
      toDTO(): InvalidAnswerJsonDTO {
        const dto: InvalidAnswerJsonDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidAnswerJson.prototype.toString = function toString(): string {
      return `SaveDraftRPC.INVALID_ANSWER_JSON()`;
    };
    
    
    export interface SurveyAlreadySubmittedDTO {
      code: string;
      thankYouPageText: SurveyLocalizedTextDTO;
    }
    
    export class SurveyAlreadySubmitted extends LeoRPCError<RPCError> {
      readonly isSurveyAlreadySubmitted: boolean = true;
    
      readonly thankYouPageText: SurveyLocalizedText;
    
      constructor(
      thankYouPageText: SurveyLocalizedText
      ) {
        const code = RPCError.SurveyAlreadySubmitted;
        super(code);
        this.thankYouPageText = thankYouPageText;
      }
    
      static fromDTO(dto: SurveyAlreadySubmittedDTO): SurveyAlreadySubmitted {
        const thankYouPageText = SurveyLocalizedText.fromDTO(getObject(dto, "thankYouPageText", InvalidResponseSurveyAlreadySubmittedThankYouPageTextError));
        return new SurveyAlreadySubmitted(
          thankYouPageText
        );
      }
    
      toDTO(): SurveyAlreadySubmittedDTO {
        const dto: SurveyAlreadySubmittedDTO = {
          code: this.code,
          thankYouPageText: this.thankYouPageText.toDTO()
        };
        return dto;
      }
    
      }
    
    SurveyAlreadySubmitted.prototype.toString = function toString(): string {
      return `SaveDraftRPC.SURVEY_ALREADY_SUBMITTED(thankYouPageText='${this.thankYouPageText}')`;
    };
    

    export type Errors = InvalidSurveyLinkId | SurveyIsClosed | InvalidQuestionId | InvalidAnswerJson | SurveyAlreadySubmitted;
    export type ErrorsDTO = InvalidSurveyLinkIdDTO | SurveyIsClosedDTO | InvalidQuestionIdDTO | InvalidAnswerJsonDTO | SurveyAlreadySubmittedDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidSurveyLinkId:
          return new InvalidSurveyLinkId();
        case RPCError.SurveyIsClosed:
          return SurveyIsClosed.fromDTO(dto as SurveyIsClosedDTO);
        case RPCError.InvalidQuestionId:
          return new InvalidQuestionId();
        case RPCError.InvalidAnswerJson:
          return new InvalidAnswerJson();
        case RPCError.SurveyAlreadySubmitted:
          return SurveyAlreadySubmitted.fromDTO(dto as SurveyAlreadySubmittedDTO);
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestSurveyLinkIdError extends SaveDraftRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveyLinkIdError";
  }
}

export class InvalidRequestCurrentQuestionIdError extends SaveDraftRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestCurrentQuestionIdError";
  }
}

export class InvalidRequestPartialAnswerJsonError extends SaveDraftRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestPartialAnswerJsonError";
  }
}

export class InvalidRequestPartialAnswerJsonNonEmptyError extends InvalidRequestPartialAnswerJsonError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestPartialAnswerJsonNonEmptyError";
  }
}


export class InvalidResponseInvalidSurveyLinkIdError extends SaveDraftRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSurveyLinkIdError";
  }
}

export class InvalidResponseSurveyIsClosedError extends SaveDraftRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyIsClosedError";
  }
}

export class InvalidResponseSurveyIsClosedSurveyIsClosedTitleTextError extends InvalidResponseSurveyIsClosedError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyIsClosedSurveyIsClosedTitleTextError";
  }
}

export class InvalidResponseSurveyIsClosedSurveyIsClosedDescriptionTextError extends InvalidResponseSurveyIsClosedError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyIsClosedSurveyIsClosedDescriptionTextError";
  }
}
export class InvalidResponseInvalidQuestionIdError extends SaveDraftRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidQuestionIdError";
  }
}

export class InvalidResponseInvalidAnswerJsonError extends SaveDraftRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidAnswerJsonError";
  }
}

export class InvalidResponseSurveyAlreadySubmittedError extends SaveDraftRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyAlreadySubmittedError";
  }
}

export class InvalidResponseSurveyAlreadySubmittedThankYouPageTextError extends InvalidResponseSurveyAlreadySubmittedError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyAlreadySubmittedThankYouPageTextError";
  }
}
